import React, { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./../../../../assets/css/purchase/form.css";

// React DateTime
import Datetime from "react-datetime";
import moment from "moment";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import ApModal from "../../../components/Modal/ApModal";
import FormAction from "../../../components/Actions/FormAction";
import ItemWhTable from "../../../components/Content/ItemWhTable";

// Model
import { whDocMasterModel } from "../../../../model/Warehouse/WHDocMasterModel";
import { whDocDetailModel } from "../../../../model/Warehouse/WHDocDetailModel";

import {
  getDataWithComp,
  getViewAp,
  formatCurrency,
  getAlert,
  getMaxWDPNo,
  randomDelay,
  parseCurrency,
  getDataWithJoin,
} from "../../../../utils/SamuiUtils";

import {
  formatDateTime,
  formatDateOnChange,
  getCreateDateTime,
} from "../../../../utils/DateUtils";

import { manageWhItemData } from "../../../../utils/WarehouseUtils";

/** ยอดหลังนับจริง (ใบ CNT): ใช้ itemCounted ที่ผู้ใช้กรอกเป็นหลัก — itemBalance อาจค้างเป็นยอดบัญชีก่อนนับ */
const getCntResultOnhand = (item) => {
  const rawC = item?.itemCounted;
  if (rawC !== null && rawC !== undefined && String(rawC).trim() !== "")
    return parseCurrency(rawC);
  return parseCurrency(item?.itemBalance ?? item?.itemOnHand ?? 0);
};

const normalizeToSqlDateTime = (input, defaultTime = "start") => {
  if (!input) return null;

  const applyTime = (m) => {
    if (defaultTime === "end") {
      return m.clone().hour(23).minute(59).second(59).millisecond(0);
    }
    return m.clone().hour(0).minute(0).second(0).millisecond(0);
  };

  let m = null;
  if (moment.isMoment(input)) {
    m = input.clone();
  } else if (input instanceof Date) {
    m = moment(input);
  } else if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // รองรับรูปแบบ UI ไทย เช่น 10-04-2569 หรือ 10-04-2569 14:35:00
    const thaiMatch = trimmed.match(
      /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (thaiMatch) {
      const [, dd, mm, yyyy, hh, min, ss] = thaiMatch;
      const yearCE = Number(yyyy) > 2400 ? Number(yyyy) - 543 : Number(yyyy);
      m = moment(
        `${yearCE}-${mm}-${dd} ${hh ?? "00"}:${min ?? "00"}:${ss ?? "00"}`,
        "YYYY-MM-DD HH:mm:ss",
        true
      );
    } else {
      m = moment(
        trimmed,
        [
          "YYYY-MM-DD HH:mm:ss.SSS",
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DD",
          moment.ISO_8601,
        ],
        true
      );
      if (!m.isValid()) m = moment(trimmed);
    }
  }

  if (!m || !m.isValid()) return null;
  const withTime =
    typeof input === "string" && input.includes(":") ? m : applyTime(m);
  return withTime.format("YYYY-MM-DD HH:mm:ss.SSS");
};

/** ใบนับสต๊อก: รอบันทึกผล / บันทึกผลแล้ว */
const WH_CNT_STATUS_PENDING = 14;
const WH_CNT_STATUS_COMPLETED = 1;

const cntHasCountedInput = (item) => {
  const raw = item?.itemCounted;
  return raw !== null && raw !== undefined && String(raw).trim() !== "";
};

const FormWDP = ({ callInitialize, mode, name, whDocNo, docNoPrefix = "WDP", editWhDocId = null }) => {
  const navigate = useNavigate();
  const [formMasterList, setFormMasterList] = useState(whDocMasterModel());
  const [formDetailList, setFormDetailList] = useState([]);
  const [itemDataList, setItemDataList] = useState([]);
  console.debug();
  const [whDataList, setWhDataList] = useState([]);
  const [apDataList, setApDataList] = useState([]);
  const [allOnhandData, setAllOnhandData] = useState([]);
  const [latestWhPerItem, setLatestWhPerItem] = useState({});
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [countHeaderStatus, setCountHeaderStatus] = useState(null);
  const [editingWhDocId, setEditingWhDocId] = useState(null);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editWhDocId]);

  const loadCountDocumentForEdit = async (docId) => {
    try {
      const headers = await getDataWithComp("WHDoc_H", `AND WHDoc_Id = ${docId}`);
      if (!headers || headers.length === 0) {
        getAlert("FAILED", "ไม่พบเอกสาร");
        return;
      }
      const h = headers[0];
      const st = Number(h.WHDoc_Status);
      setCountHeaderStatus(Number.isFinite(st) ? st : null);
      setEditingWhDocId(docId);

      setFormMasterList({
        ...whDocMasterModel(),
        whDocId: h.WHDoc_Id,
        whDocNo: h.WHDoc_No,
        whDocDate: formatDateToThai(h.WHDoc_Date) || "",
        whDocTransDate: formatDateToThai(h.WHDoc_TransDate) || "",
        whDocCreatedDate: formatDateTimeToThai(h.WHDoc_CreatedDate) || "",
        whDocCreatedBy: h.WHDoc_CreatedBy || "",
        whDocRemark: h.WHDoc_Remark || "",
        whDocCompId: String(h.Comp_Id ?? window.localStorage.getItem("company") ?? ""),
        whDocCountCompletedDate: h.WHDoc_CountCompletedDate
          ? formatDateTimeToThai(h.WHDoc_CountCompletedDate)
          : "",
        whDocCountCompletedBy: h.WHDoc_CountCompletedBy || "",
      });

      const compId = window.localStorage.getItem("company");
      const detailRes = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/get-wh-doc-d-by-wh-doc-id`,
        { wh_doc_id: docId, comp_id: compId },
        { headers: { key: process.env.REACT_APP_ANALYTICS_KEY } }
      );
      const details = detailRes.data?.status === "OK" ? detailRes.data.data : [];
      const lines = (Array.isArray(details) ? details : []).map((r, index) => {
        const onHand = parseCurrency(r.Item_Price_Unit ?? 0) || 0;
        const tot = r.Item_Total;
        const hasTot = tot != null && tot !== "";
        const countedNum = hasTot ? parseCurrency(tot) : NaN;
        const countedDisplay =
          hasTot && Number.isFinite(countedNum) ? formatNumberInput(String(countedNum)) : "";
        const docT = r.Doc_Type || null;
        const qtyAdj = parseCurrency(r.Item_Qty) || 0;
        const fwh = r.F_WH_Id ?? h.F_WH_Id ?? null;
        return {
          line: index + 1,
          itemId: r.Item_Id,
          itemCode: r.Item_Code,
          itemName: r.Item_Name,
          itemUnit: r.Item_Unit,
          fWhId: fwh,
          itemOnHand: onHand,
          itemBalance: hasTot && Number.isFinite(countedNum) ? countedNum : onHand,
          itemCounted: countedDisplay,
          itemQty: formatNumberInput(String(qtyAdj)),
          itemIncrease: formatCurrency(0),
          itemDecrease: formatCurrency(0),
          docType: !countedDisplay && (!docT || docT === "NC") ? null : docT,
          itemPriceUnit: 0,
          itemTotal: 0,
          itemIsReturn: null,
          itemReturnDate: null,
          fZoneId: null,
          fLtId: null,
          tWhId: null,
          refDocId: null,
        };
      });
      setFormDetailList(lines);
    } catch (e) {
      getAlert("FAILED", e?.message || "โหลดเอกสารไม่สำเร็จ");
    }
  };

  const initialize = async () => {
    const whDataList = await getDataWithComp(
      "Tb_Set_WH",
      `AND WH_Status = 'Y' ORDER BY WH_Name ASC`
    );
    if (whDataList && whDataList.length > 0) {
      setWhDataList(whDataList);
    }

    const apDataList = await getViewAp();
    if (apDataList && apDataList.length > 0) {
      setApDataList(apDataList);
    }

    await loadAllItems();

    const onhandAll = await getDataWithComp("API_1101_WH_ITEM_ONHAND", " ORDER BY Item_Id ASC");
    setAllOnhandData(onhandAll || []);

    const historyData = await getDataWithComp(
      "WH_ITEM_STC",
      "AND WH_Id IS NOT NULL ORDER BY STC_SEQ DESC"
    );
    const latestMap = {};
    for (const row of historyData || []) {
      const itemId = Number(row.Item_Id);
      if (!latestMap[itemId]) latestMap[itemId] = Number(row.WH_Id);
    }
    setLatestWhPerItem(latestMap);

    if (docNoPrefix === "CNT") {
      if (editWhDocId) {
        const parseId = parseInt(String(editWhDocId), 10);
        if (Number.isFinite(parseId)) await loadCountDocumentForEdit(parseId);
      } else {
        setCountHeaderStatus(null);
        setEditingWhDocId(null);
        setFormMasterList(whDocMasterModel());
        setFormDetailList([]);
        setSelectedFiles([]);
      }
    }
  };

  const loadAllItems = async () => {
    const companyId = localStorage.getItem("company");
    try {
      const data = await getDataWithJoin(
        `
        v.Item_Id, v.Item_Barcode, v.Item_Code,
        v.Item_Name, v.Item_Name_EN, v.Item_Short_Name,
        v.Item_Unit_IN, v.Item_Unit_ST, v.Item_Unit_OUT,
        v.Item_Cost, v.Item_Price, v.Item_PriceA, v.Item_PriceB,
        v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line,
        ISNULL((SELECT SUM(wh.Item_Onhand) FROM API_1101_WH_ITEM_ONHAND wh WHERE wh.Item_Id = v.Item_Id AND wh.Comp_Id = ${companyId}), 0) AS Item_Onhand
      `,
        `View_Set_Item v`,
        `LEFT JOIN Tb_Set_ItmGroup grp ON (grp.Group_Id = v.Item_Group AND grp.Comp_Id = ${companyId} AND grp.Group_Code = 'G')`,
        `WHERE v.Comp_Id = ${companyId} AND v.Item_Status = 'Y'
         ORDER BY v.Item_Short_Name, v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line`
      );
      if (data && data.length > 0) setItemDataList(data);
    } catch (error) {
      console.error("Error loading items:", error);
    }
  };

  const handleWarehouseChangePerRow = async (index, whId) => {
    const updatedList = [...formDetailList];
    const parsedWhId = whId ? Number(whId) : null;
    updatedList[index] = { ...updatedList[index], fWhId: parsedWhId };

    if (parsedWhId) {
      try {
        const onhandData = await getDataWithComp(
          "API_1101_WH_ITEM_ONHAND",
          `AND WH_Id = ${parsedWhId} AND Item_Id = ${updatedList[index].itemId}`
        );
        const onhand =
          onhandData && onhandData.length > 0
            ? parseCurrency(onhandData[0].Item_Onhand) || 0
            : 0;
        updatedList[index].itemOnHand = onhand;
        updatedList[index].itemBalance = onhand;
      } catch (e) {
        updatedList[index].itemOnHand = 0;
        updatedList[index].itemBalance = 0;
      }
    } else {
      updatedList[index].itemOnHand = 0;
      updatedList[index].itemBalance = 0;
    }

    setFormDetailList(updatedList);
  };

  const handleChangeMaster = (e) => {
    const { name, value } = e.target;
    // อัปเดตค่าใน formMasterList
    setFormMasterList((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ฟังก์ชันแปลงวันที่จากรูปแบบต่างๆ เป็นรูปแบบ dd-mm-yyyy (พ.ศ.)
  const formatDateTimeToThai = (dateString) => {
    if (!dateString) return '';
    const date = moment(dateString);
    if (!date.isValid()) return '';
    const day = date.date();
    const month = date.month() + 1;
    const year = date.year() + 543;
    const hh = String(date.hours()).padStart(2, '0');
    const mm = String(date.minutes()).padStart(2, '0');
    const ss = String(date.seconds()).padStart(2, '0');
    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year} ${hh}:${mm}:${ss}`;
  };

  const formatDateToThai = (dateString) => {
    if (!dateString) return '';
    
    // ถ้าเป็นรูปแบบ dd-mm-yyyy (พ.ศ.) อยู่แล้ว ให้ return ตามเดิม
    if (typeof dateString === 'string') {
      const thaiDatePattern = /^\d{2}-\d{2}-\d{4}$/;
      if (thaiDatePattern.test(dateString)) {
        const parts = dateString.split('-');
        const year = parseInt(parts[2], 10);
        // ถ้าเป็นปีพ.ศ. (มากกว่า 2400) ให้ return ตามเดิม
        if (year > 2400) {
          return dateString;
        }
      }
    }
    
    // แปลงจาก datetime format หรือรูปแบบอื่นๆ
    const date = moment(dateString);
    if (!date.isValid()) return '';
    
    const day = date.date();
    const month = date.month() + 1; // moment ใช้ month 0-11
    const year = date.year() + 543; // แปลงเป็นพ.ศ.
    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
  };

  // ฟังก์ชันแปลงวันที่จากรูปแบบ dd-mm-yyyy (พ.ศ.) เป็น moment object (ค.ศ.) สำหรับ react-datetime
  const parseThaiDateToMoment = (dateString) => {
    if (!dateString) return null;
    // ถ้าเป็น moment object อยู่แล้ว ให้ return ตามเดิม
    if (moment.isMoment(dateString)) return dateString;
    
    // แปลงเป็นรูปแบบไทยก่อน
    const thaiDate = formatDateToThai(dateString);
    
    // ถ้าเป็น string ในรูปแบบ dd-mm-yyyy (พ.ศ.)
    if (typeof thaiDate === 'string' && thaiDate) {
      const parts = thaiDate.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // moment ใช้ month 0-11
        const buddhistYear = parseInt(parts[2], 10);
        const christianYear = buddhistYear - 543;
        return moment({ year: christianYear, month: month, date: day });
      }
    }
    
    // ถ้าไม่ใช่รูปแบบที่คาดหวัง ให้ใช้ moment parse ตามปกติ
    return moment(dateString);
  };

  // ฟังก์ชันแปลง moment object (ค.ศ.) เป็น string dd-mm-yyyy (พ.ศ.)
  const formatMomentToThaiDate = (momentObj) => {
    if (!momentObj || !moment.isMoment(momentObj) || !momentObj.isValid()) {
      return '';
    }
    const day = momentObj.date();
    const month = momentObj.month() + 1; // moment ใช้ month 0-11
    const year = momentObj.year() + 543; // แปลงเป็นพ.ศ.
    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
  };

  const formatNumberInput = (raw) => {
    if (raw === null || raw === undefined) return "";
    const cleaned = String(raw).replace(/,/g, "");
    if (cleaned === "") return "";
    if (!/^\d*\.?\d*$/.test(cleaned)) return String(raw);
    const [intPart = "", decPart] = cleaned.split(".");
    const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (decPart !== undefined) {
      const decTrim = decPart.slice(0, 2);
      return `${intFmt}.${decTrim}`;
    }
    return intFmt;
  };

  const handleChangeDateMaster = (value, name) => {
    // value จาก react-datetime จะเป็น moment object (ค.ศ.)
    if (value && moment.isMoment(value)) {
      // แปลงเป็นรูปแบบ dd-mm-yyyy (พ.ศ.)
      const thaiDate = formatMomentToThaiDate(value);
      setFormMasterList((prev) => ({
        ...prev,
        [name]: thaiDate,
      }));
    } else {
      // ถ้าไม่ใช่ moment object ใช้ formatDateOnChange ตามเดิม
      const newValue = value ? value.format("YYYY-MM-DD") : value;
      setFormMasterList((prev) => ({
        ...prev,
        [name]: formatDateOnChange(newValue),
      }));
    }
  };

  const handleChangeWarehouse = async (value) => {
    setFormDetailList([]);
    setItemDataList([]);

    const whId = parseCurrency(value);
    const companyId = localStorage.getItem("company");

    console.debug("🔍 Loading warehouse items:", { whId, companyId });

    try {
      const data = await getDataWithJoin(
        `
        v.Item_Id, v.Item_Barcode, v.Item_Code, 
        v.Item_Name, v.Item_Name_EN, v.Item_Short_Name, 
        v.Item_Unit_IN, v.Item_Unit_ST, v.Item_Unit_OUT, 
        v.Item_Cost, v.Item_Price, v.Item_PriceA, v.Item_PriceB, 
        v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line,
        ISNULL(wh.Item_Onhand, 0) AS Item_Onhand
      `,
        `View_Set_Item v`,
        `
        LEFT JOIN Tb_Set_ItmGroup grp ON (grp.Group_Id = v.Item_Group AND grp.Comp_Id = ${companyId} AND grp.Group_Code = 'G')
        ${
          whId
            ? `LEFT JOIN (
          SELECT wh.*, ROW_NUMBER() OVER (PARTITION BY wh.Item_Id ORDER BY wh.Item_Onhand DESC) AS rn
          FROM API_1101_WH_ITEM_ONHAND wh
          WHERE wh.WH_Id = ${whId} AND wh.Comp_Id = ${companyId}
        ) wh ON (wh.Item_Id = v.Item_Id AND wh.rn = 1)`
            : ""
        }
      `,
        `
        WHERE v.Comp_Id = ${companyId} 
        AND v.Item_Status = 'Y'
        ORDER BY v.Item_Short_Name, v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line
      `
      );

      console.debug("✅ Data loaded:", data?.length, "items");
      console.debug("Sample item:", data?.[0]);

      if (data && data.length > 0) {
        setItemDataList(data);
      } else {
        console.warn("⚠️ No items found for warehouse:", whId);
      }
    } catch (error) {
      console.error("❌ Error loading warehouse items:", error);
      alert(`เกิดข้อผิดพลาด: ${error.message || error}`);
    }

    setSelectedWarehouse(value);
  };

  const handleQuantityChange = (index, type, value, onHand) => {
    // ตรวจสอบว่าค่าที่กรอกเข้ามาเป็นตัวเลขหรือตัวเลขที่มีจุดทศนิยม (รองรับ comma จาก formatNumericInput)
    const rawValue = String(value).replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(rawValue)) {
      return;
    }

    const updatedList = [...formDetailList];

    // ใบนับสต๊อก: กรอกจำนวนจริง แล้วระบบคำนวณว่าเพิ่มหรือลด
    if (type === "count") {
      const countedVal = parseCurrency(value);
      const onhandVal = parseCurrency(onHand);
      updatedList[index].itemCounted = value;
      if (countedVal > onhandVal) {
        updatedList[index].docType = "AI";
        updatedList[index].itemQty = countedVal - onhandVal;
      } else if (countedVal < onhandVal) {
        updatedList[index].docType = "AO";
        updatedList[index].itemQty = onhandVal - countedVal;
      } else {
        updatedList[index].docType = null;
        updatedList[index].itemQty = 0;
      }
      updatedList[index].itemBalance = countedVal;
      setFormDetailList(updatedList);
      return;
    }

    // หยอดข้อมูล เพิ่ม/ลด รายละเอียดสินค้า แยกตามประเภท
    if (type === "increase") {
      updatedList[index].itemIncrease = value;
      updatedList[index].itemDecrease = null;
      updatedList[index].itemBalance =
        parseCurrency(onHand) + parseCurrency(value);
      updatedList[index].itemQty = parseCurrency(value);
    } else if (type === "decrease") {
      // ดักไม่ให้ปรับปรุงเกินกว่าสินค้าคงเหลือ เฉพาะตอนปรับลดสินค้า
      if (value > onHand) {
        getAlert("FAILED", "กรุณาระบุจำนวนไม่เกินคงเหลือ");
        updatedList[index].itemIncrease = null;
        updatedList[index].itemDecrease = null;
        updatedList[index].docType = null;
        updatedList[index].itemBalance = parseCurrency(onHand);
        setFormDetailList(updatedList);
        return;
      }

      updatedList[index].itemIncrease = null;
      updatedList[index].itemDecrease = value;
      updatedList[index].itemQty = parseCurrency(value);
      updatedList[index].itemBalance =
        parseCurrency(onHand) - parseCurrency(value);
    }

    updatedList[index].docType = type === "increase" ? "AI" : "AO";
    setFormDetailList(updatedList);
  };

  const handleFocus = (index, field) => {
    const updatedList = [...formDetailList];
    const raw = parseCurrency(updatedList[index][field]);
    updatedList[index][field] = Number.isFinite(raw) ? String(raw) : "0";
    setFormDetailList(updatedList);
  };

  const handleBlur = (index, field, value) => {
    const updatedList = [...formDetailList];
    if (field === 'itemCounted') {
      const num = parseCurrency(value);
      const n = Number.isFinite(num) ? num : 0;
      const [intPart, decPart = ""] = n.toFixed(2).split(".");
      const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      updatedList[index][field] = `${intFmt}.${decPart}`;
    } else {
      updatedList[index][field] = formatNumberInput(value);
    }
    setFormDetailList(updatedList);
  };

  // SET AP
  const [showApModal, setShowApModal] = useState(false);
  const handleApShow = () => setShowApModal(true);
  const handleApClose = () => setShowApModal(false);
  const onRowSelectAp = (apSelected) => {
    try {
      setFormMasterList({
        ...formMasterList,
        whDocCustId: apSelected.AP_Id,
        whDocCustCode: apSelected.AP_Code,
        whDocCustName: apSelected.AP_Name,
      });
      handleApClose(); // ปิด modal หลังจากเลือก
    } catch (error) {
      getAlert("FAILED", error);
    }
  };

  // SET ITEM
  const [showItemModal, setShowItemModal] = useState(false);
  const handleItemShow = () => setShowItemModal(true);
  const handleItemClose = () => setShowItemModal(false);
  const onRowSelectItem = (itemsSelected) => {
    try {
      // ใช้ Set เพื่อกรอง Item_Id ที่ซ้ำกัน
      const uniqueItems = Array.from(
        new Set(itemsSelected.map((item) => item.Item_Id))
      ).map((id) => {
        return itemsSelected.find((item) => item.Item_Id === id);
      });

      // อัปเดตรายการโดยคงค่าของสินค้าที่เคยมีอยู่
      const updatedFormDetailList = uniqueItems.map((itemSelected) => {
        // ค้นหาว่ามีสินค้าอยู่ใน formDetailList เดิมหรือไม่
        const existingItem = formDetailList.find(
          (item) => item.itemId === itemSelected.Item_Id
        );

        const availableWhIds = [...new Set(
          allOnhandData
            .filter((oh) => Number(oh.Item_Id) === Number(itemSelected.Item_Id))
            .map((oh) => Number(oh.WH_Id))
        )];
        let defaultWhId =
          existingItem?.fWhId ||
          latestWhPerItem[Number(itemSelected.Item_Id)] ||
          (availableWhIds.length === 1 ? availableWhIds[0] : null);
        const whOnhandEntry = defaultWhId
          ? allOnhandData.find(
              (oh) => Number(oh.Item_Id) === Number(itemSelected.Item_Id) && Number(oh.WH_Id) === defaultWhId
            )
          : null;
        const defaultOnhand = whOnhandEntry ? parseCurrency(whOnhandEntry.Item_Onhand) || 0 : 0;

        return {
          line: existingItem?.line || null,
          itemId: itemSelected.Item_Id,
          itemCode: itemSelected.Item_Code,
          itemName: itemSelected.Item_Name,
          itemQty: formatCurrency(0),
          itemIncrease: formatCurrency(0),
          itemDecrease: formatCurrency(0),
          itemUnit: existingItem ? existingItem.itemUnit : itemSelected.Item_Unit_ST,
          fWhId: defaultWhId,
          itemOnHand: defaultOnhand,
          itemBalance: defaultOnhand,
          itemCounted: "0.00",
          itemPriceUnit: 0,
          itemTotal: 0,
          docType: null,
          itemIsReturn: null,
          itemReturnDate: null,
          fZoneId: itemSelected.F_Zone_Id || null,
          fLtId: itemSelected.F_LT_Id || null,
          tWhId: itemSelected.T_WH_Id || null,
          refDocId: itemSelected.Ref_DocID || null,
        };
      });

      setFormDetailList(updatedFormDetailList);
      handleItemClose();
    } catch (error) {
      getAlert("FAILED", error);
    }
  };
  const handleRemoveRow = (index) => {
    const newList = formDetailList.filter((_, i) => i !== index);
    setFormDetailList(newList);
  };

  const handleUploadAttachments = async (whDocId) => {
    const pendingFiles = selectedFiles.filter((file) => file.isPending);
    if (pendingFiles.length === 0) return;

    try {
      const filesWithBase64 = pendingFiles.map((file) => ({
        id: file.id,
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl: file.dataUrl,
        isPending: true,
        displayName: file.name,
      }));

      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload-file`,
        {
          comp_id: localStorage.getItem("company"),
          doc_id: whDocId,
          DocCode_ID: 18, // รหัสประเภทเอกสาร ปรับตามระบบ
          upload_by: localStorage.getItem("name") || "system",
          file_status: "Y",
          files: filesWithBase64,
          doc_no_ref: null,
        },
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );

      if (response.data.status === "OK") {
        setSelectedFiles((prev) =>
          prev
            .filter((file) => !file.isPending)
            .concat(
              response.data.files.map((file) => ({
                F_Id: file.File_Id,
                fileName: file.fileName,
                File_Url: file.fileUrl,
                uploadDate: file.uploadDate,
                uploadBy: file.uploadBy,
              }))
            )
        );
        // getAlert("OK", "อัปโหลดไฟล์สำเร็จ");
      } else {
        throw new Error(response.data.message || "การอัปโหลดล้มเหลว");
      }
    } catch (error) {
      console.error("UPLOAD ERROR:", error);
      getAlert("FAILED", `การอัปโหลดไฟล์ล้มเหลว: ${error.message}`);
    }
  };

  const saveCountDocument = async (kind) => {
    const isDraft = kind === "draft";
    const isFinalize = kind === "finalize";
    if (!isDraft && !isFinalize) return;

    try {
      await randomDelay();

      if (docNoPrefix === "CNT" && countHeaderStatus === WH_CNT_STATUS_COMPLETED) {
        getAlert("FAILED", "เอกสารนี้บันทึกผลแล้ว ไม่สามารถแก้ไขได้");
        return;
      }

      if (!formDetailList || formDetailList.length === 0) {
        getAlert("FAILED", "กรุณาเพิ่มรายละเอียดสินค้าที่จะปรับปรุง");
        return;
      }

      const itemsToSubmit = formDetailList;
      const compId = window.localStorage.getItem("company");
      const userName = window.localStorage.getItem("name") || "system";
      const editingId =
        editingWhDocId ||
        (editWhDocId != null && editWhDocId !== ""
          ? parseInt(String(editWhDocId), 10)
          : null);

      for (const item of itemsToSubmit) {
        if (!item.fWhId) {
          getAlert("FAILED", `กรุณาเลือกคลังของสินค้า ${item.itemName}`);
          return;
        }
      }

      if (isFinalize) {
        for (const item of itemsToSubmit) {
          if (!cntHasCountedInput(item)) {
            getAlert("FAILED", `กรุณากรอกผลการนับของสินค้า ${item.itemName}`);
            return;
          }
        }
        for (const item of itemsToSubmit) {
          const isCntNoChange = docNoPrefix === "CNT" && !item.docType;
          if (!isCntNoChange) {
            if (!item.itemQty || parseCurrency(item.itemQty) === 0) {
              getAlert("FAILED", `กรุณากรอกจำนวนปรับปรุงของสินค้า ${item.itemName}`);
              return;
            }
            if (!item.docType) {
              getAlert("FAILED", `กรุณาเลือกประเภทเพิ่ม/ลดของสินค้า ${item.itemName}`);
              return;
            }
          }
        }

        const hasCountedZero = itemsToSubmit.some((item) => {
          const counted = getCntResultOnhand(item);
          const isCountedZero = Number(counted) === 0;
          return isCountedZero;
        });

        if (hasCountedZero) {
          const confirmResult = await Swal.fire({
            icon: "warning",
            title: "",
            html: `
              <div style="text-align:center; line-height:1.5;">
                <span style="display:block; font-size:22px; font-weight:500;">คุณต้องการบันทึก</span>
                <span style="display:block; font-size:22px; font-weight:500;">จำนวนที่นับได้ เป็น 0</span>
                <span style="display:block; font-size:22px; font-weight:500;">แน่หรือไม่</span>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "ยืนยัน",
            cancelButtonText: "ยกเลิก",
            confirmButtonColor: "#E13F57",
            cancelButtonColor: "#6b7280",
          });
          if (!confirmResult.isConfirmed) return;
        }
      }

      const findMaxWDPNo = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type = '3' ORDER BY WHDoc_No DESC`
      );
      const maxWDP = getMaxWDPNo(findMaxWDPNo, docNoPrefix);
      let docNoForStock = maxWDP;

      const completionSql = normalizeToSqlDateTime(getCreateDateTime());
      const completionBy = userName;

      const resolveWhDocIdFromCreateResponse = async (response, whDocNo) => {
        let whDocId = null;
        const responseData = response.data?.data || [];
        if (responseData.length > 0 && responseData[0].WHDoc_Id) {
          whDocId = parseCurrency(responseData[0].WHDoc_Id, 10);
        } else {
          const getWhDocIdResponse = await Axios.post(
            `${process.env.REACT_APP_API_URL}/api/get-by-wh-doc-no`,
            {
              table: "WHDoc_H",
              wh_doc_no: whDocNo,
              comp_id: compId,
            },
            { headers: { key: process.env.REACT_APP_ANALYTICS_KEY } }
          );
          if (getWhDocIdResponse?.data?.length > 0) {
            whDocId = parseCurrency(getWhDocIdResponse.data[0].WHDoc_Id, 10);
          }
        }
        return whDocId;
      };

      const runDetailInserts = async (whDocId, docNoRef) => {
        const detailPromises = itemsToSubmit.map(async (item, index) => {
          const isCntNoChange =
            docNoPrefix === "CNT" &&
            (!item.docType || (isDraft && !cntHasCountedInput(item)));
          const docType = isCntNoChange ? "NC" : item.docType;
          const itemQty =
            isCntNoChange && isDraft && !cntHasCountedInput(item)
              ? 0
              : isCntNoChange
                ? getCntResultOnhand(item)
                : parseCurrency(item.itemQty);

          if (isFinalize && !isCntNoChange) {
            const stockResult = await manageWhItemData(
              item.itemId,
              item.itemCode,
              item.itemName,
              item.fWhId || null,
              item.docType,
              parseCurrency(item.itemQty),
              whDocId,
              docNoRef,
              docNoRef,
              formMasterList.whDocRemark,
              docNoPrefix === "CNT" ? getCntResultOnhand(item) : null
            );
            if (stockResult === null) {
              throw new Error(`ปรับปรุงสต๊อกไม่สำเร็จสำหรับสินค้า ${item.itemName}`);
            }
          } else if (isFinalize && isCntNoChange && docNoPrefix === "CNT") {
            // NC (เท่าเดิม): ไม่ปรับ onhand แต่บันทึก STC เพื่อให้เห็นใน stock-detail
            await manageWhItemData(
              item.itemId,
              item.itemCode,
              item.itemName,
              item.fWhId || null,
              "NC",
              0,
              whDocId,
              docNoRef,
              docNoRef,
              formMasterList.whDocRemark,
              getCntResultOnhand(item) // force balance = ยอดที่นับได้ (เท่าเดิม)
            );
          }

          const itemTotalVal =
            docNoPrefix === "CNT"
              ? cntHasCountedInput(item)
                ? getCntResultOnhand(item)
                : null
              : parseCurrency(item.itemTotal);

          const formDetailData = {
            wh_doc_id: whDocId,
            line: index + 1,
            item_id: item.itemId,
            item_code: item.itemCode,
            item_name: item.itemName,
            item_qty: itemQty,
            item_unit: item.itemUnit,
            item_price_unit:
              docNoPrefix === "CNT"
                ? parseCurrency(item.itemOnHand ?? 0)
                : parseCurrency(item.itemPriceUnit),
            item_total: itemTotalVal,
            doc_type: docType,
            f_wh_id: item.fWhId || null,
            f_zone_id: item.fZoneId,
            f_lt_id: item.fLtId,
            t_wh_id: item.tWhId,
            t_zone_id: item.fZoneId,
            t_lt_id: item.fLtId,
            ref_doc_id: item.refDocId,
            item_is_return: item.itemIsReturn,
            item_return_date: normalizeToSqlDateTime(item.itemReturnDate, "start"),
          };

          const detailResponse = await Axios.post(
            `${process.env.REACT_APP_API_URL}/api/create-wh-doc-d`,
            formDetailData,
            { headers: { key: process.env.REACT_APP_ANALYTICS_KEY } }
          );
          if (detailResponse.data.status !== "OK") {
            throw new Error(
              `สร้างรายละเอียดเอกสารไม่สำเร็จ: ${detailResponse.data.message || ""}`
            );
          }
        });
        await Promise.all(detailPromises);
      };

      let whDocId = null;

      if (editingId && Number.isFinite(editingId)) {
        whDocId = editingId;
        docNoForStock = formMasterList.whDocNo || maxWDP;

        const delRes = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/delete-wh-doc-d-by-wh-doc-id`,
          { wh_doc_id: whDocId, comp_id: compId },
          { headers: { key: process.env.REACT_APP_ANALYTICS_KEY } }
        );
        if (delRes.data?.status !== "OK") {
          throw new Error(delRes.data?.message || "ลบรายละเอียดเดิมไม่สำเร็จ");
        }

        const metaRes = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/update-wh-doc-h-meta`,
          {
            wh_doc_id: whDocId,
            comp_id: compId,
            wh_doc_status: isDraft ? WH_CNT_STATUS_PENDING : WH_CNT_STATUS_COMPLETED,
            wh_doc_remark: formMasterList.whDocRemark,
            wh_doc_date: normalizeToSqlDateTime(formMasterList.whDocDate, "start"),
            wh_doc_trans_date: normalizeToSqlDateTime(formMasterList.whDocTransDate, "start"),
            wh_doc_count_completed_date: isDraft ? null : completionSql,
            wh_doc_count_completed_by: isDraft ? null : completionBy,
            wh_doc_updated_by: userName,
          },
          { headers: { key: process.env.REACT_APP_ANALYTICS_KEY } }
        );
        if (metaRes.data?.status !== "OK") {
          throw new Error(metaRes.data?.message || "อัปเดตหัวเอกสารไม่สำเร็จ");
        }

        await runDetailInserts(whDocId, docNoForStock);
      } else {
        const formMasterData = {
          wh_doc_no: maxWDP,
          wh_doc_type: 3,
          wh_doc_cust_id: parseCurrency(formMasterList.whDocCustId, 10),
          wh_doc_date: normalizeToSqlDateTime(formMasterList.whDocDate, "start"),
          wh_doc_trans_date: normalizeToSqlDateTime(
            formMasterList.whDocTransDate,
            "start"
          ),
          wh_doc_created_date: normalizeToSqlDateTime(
            formMasterList.whDocCreatedDate,
            "start"
          ),
          wh_doc_created_by: formMasterList.whDocCreatedBy,
          wh_doc_status: isDraft ? WH_CNT_STATUS_PENDING : WH_CNT_STATUS_COMPLETED,
          wh_doc_seq: formatDateTime(new Date()),
          wh_doc_remark: formMasterList.whDocRemark,
          wh_doc_comp_id: formMasterList.whDocCompId,
          wh_doc_show_front: null,
          ref_doc_id: formMasterList.refDocId,
          f_wh_id: itemsToSubmit[0]?.fWhId || null,
          wh_doc_count_completed_date: isDraft ? null : completionSql,
          wh_doc_count_completed_by: isDraft ? null : completionBy,
        };

        const response = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/create-wh-doc-h`,
          formMasterData,
          { headers: { key: process.env.REACT_APP_ANALYTICS_KEY } }
        );

        if (response.data.status !== "OK") {
          throw new Error(response.data?.message || "สร้างหัวเอกสารไม่สำเร็จ");
        }

        whDocId = await resolveWhDocIdFromCreateResponse(response, formMasterData.wh_doc_no);
        docNoForStock = formMasterData.wh_doc_no;

        if (!whDocId) {
          throw new Error("ไม่พบ WHDoc_Id หลังสร้างใบนับสต๊อก");
        }

        await runDetailInserts(whDocId, docNoForStock);
      }

      await handleUploadAttachments(whDocId);

      if (isDraft) {
        const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
        if (originUrl) {
          localStorage.removeItem("TreasuryDocumentsOrigin");
          navigate(originUrl);
        } else {
          navigate(-1);
        }
      } else {
        setCountHeaderStatus(WH_CNT_STATUS_COMPLETED);
        setFormMasterList((prev) => ({
          ...prev,
          whDocId,
          whDocNo: docNoForStock,
          whDocCountCompletedDate: formatDateTimeToThai(completionSql) || prev.whDocCountCompletedDate,
          whDocCountCompletedBy: completionBy,
        }));
        const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
        if (originUrl) {
          localStorage.removeItem("TreasuryDocumentsOrigin");
          navigate(originUrl);
        } else {
          callInitialize();
        }
      }
    } catch (error) {
      getAlert("FAILED", error.response?.data?.message || error.message);
    }
  };

  const handleSubmit = async () => {
    try {
      if (docNoPrefix === "CNT") {
        await saveCountDocument("finalize");
        return;
      }

      // ทดสอบหน่วงเวลาก่อนบันทึก (ทดสอบป้องกันการซ้ำของ Doc_No)
      await randomDelay();

      const findMaxWDPNo = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type = '3' ORDER BY WHDoc_No DESC`
      );
      const maxWDP = getMaxWDPNo(findMaxWDPNo, docNoPrefix);
      let newMaxWDP = maxWDP;
      // ตรวจสอบค่า formMasterList.apID และ formMasterList.apCode

      // ตรวจสอบว่า formDetailList มีค่าหรือมีความยาวเป็น 0
      if (!formDetailList || formDetailList.length === 0) {
        getAlert("FAILED", "กรุณาเพิ่มรายละเอียดสินค้าที่จะปรับปรุง");
        return;
      }

      const itemsToSubmit = formDetailList;

      // ตรวจสอบค่าภายใน itemsToSubmit (ข้ามรายการที่นับเท่าเดิม/0 สำหรับ CNT)
      for (const item of itemsToSubmit) {
        if (!item.fWhId) {
          getAlert("FAILED", `กรุณาเลือกคลังของสินค้า ${item.itemName}`);
          return;
        }
        const isCntNoChange = docNoPrefix === "CNT" && !item.docType;
        if (!isCntNoChange) {
          if (!item.itemQty || parseCurrency(item.itemQty) === 0) {
            getAlert("FAILED", `กรุณากรอกจำนวนปรับปรุงของสินค้า ${item.itemName}`);
            return;
          }
          if (!item.docType) {
            getAlert("FAILED", `กรุณาเลือกประเภทเพิ่ม/ลดของสินค้า ${item.itemName}`);
            return;
          }
        }
      }

      // // ข้อมูลหลักที่จะส่งไปยัง API
      const formMasterData = {
        wh_doc_no: newMaxWDP,
        wh_doc_type: 3,
        wh_doc_cust_id: parseCurrency(formMasterList.whDocCustId, 10),
        wh_doc_date: normalizeToSqlDateTime(formMasterList.whDocDate, "start"),
        wh_doc_trans_date: normalizeToSqlDateTime(
          formMasterList.whDocTransDate,
          "start"
        ),
        wh_doc_created_date: normalizeToSqlDateTime(
          formMasterList.whDocCreatedDate,
          "start"
        ),
        wh_doc_created_by: formMasterList.whDocCreatedBy,
        wh_doc_status: parseCurrency("1", 10),
        wh_doc_seq: formatDateTime(new Date()),
        wh_doc_remark: formMasterList.whDocRemark,
        wh_doc_comp_id: formMasterList.whDocCompId,
        wh_doc_show_front: null,
        ref_doc_id: formMasterList.refDocId,
        f_wh_id: docNoPrefix === "CNT" ? (itemsToSubmit[0]?.fWhId || null) : null,
      };

      // ส่งข้อมูลหลักไปยัง API
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/create-wh-doc-h`,
        formMasterData,
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );

      if (response.data.status === "OK") {
        let whDocId = null;
        const responseData = response.data?.data || [];
        if (responseData.length > 0 && responseData[0].WHDoc_Id) {
          whDocId = parseCurrency(responseData[0].WHDoc_Id, 10);
        } else {
          const getWhDocIdResponse = await Axios.post(
            `${process.env.REACT_APP_API_URL}/api/get-by-wh-doc-no`,
            {
              table: "WHDoc_H",
              wh_doc_no: formMasterData.wh_doc_no,
              comp_id: window.localStorage.getItem("company"),
            },
            {
              headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
            }
          );
          if (getWhDocIdResponse && getWhDocIdResponse.data.length > 0) {
            whDocId = parseCurrency(getWhDocIdResponse.data[0].WHDoc_Id, 10);
          }
        }

        if (!whDocId) {
          throw new Error("ไม่พบ WHDoc_Id หลังสร้างใบเบิกสินค้า");
        }

        const detailPromises = itemsToSubmit.map(async (item, index) => {
            const isCntNoChange = docNoPrefix === "CNT" && !item.docType;
            const docType = isCntNoChange ? "NC" : item.docType;
            const itemQty = isCntNoChange
              ? getCntResultOnhand(item)
              : parseCurrency(item.itemQty);

            const formDetailData = {
              wh_doc_id: whDocId,
              line: index + 1,
              item_id: item.itemId,
              item_code: item.itemCode,
              item_name: item.itemName,
              item_qty: itemQty,
              item_unit: item.itemUnit,
              item_price_unit: docNoPrefix === "CNT"
                ? parseCurrency(item.itemOnHand ?? 0)
                : parseCurrency(item.itemPriceUnit),
              item_total: docNoPrefix === "CNT"
                ? getCntResultOnhand(item)
                : parseCurrency(item.itemTotal),
              doc_type: docType,
              f_wh_id: item.fWhId || null,
              f_zone_id: item.fZoneId,
              f_lt_id: item.fLtId,
              t_wh_id: item.tWhId,
              t_zone_id: item.fZoneId,
              t_lt_id: item.fLtId,
              ref_doc_id: item.refDocId,
              item_is_return: item.itemIsReturn,
              item_return_date: normalizeToSqlDateTime(item.itemReturnDate, "start"),
            };

            // เรียก manageWhItemData เฉพาะรายการที่มีการปรับปรุงสต๊อกจริง
            if (!isCntNoChange) {
              const stockResult = await manageWhItemData(
                item.itemId,
                item.itemCode,
                item.itemName,
                item.fWhId || null,
                item.docType,
                parseCurrency(item.itemQty),
                whDocId,
                newMaxWDP,
                newMaxWDP,
                formMasterList.whDocRemark,
                docNoPrefix === "CNT" ? getCntResultOnhand(item) : null
              );
              if (stockResult === null) {
                throw new Error(`ปรับปรุงสต๊อกไม่สำเร็จสำหรับสินค้า ${item.itemName}`);
              }
            }

            const detailResponse = await Axios.post(
              `${process.env.REACT_APP_API_URL}/api/create-wh-doc-d`,
              formDetailData,
              {
                headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
              }
            );
            if (detailResponse.data.status !== "OK") {
              throw new Error(
                `สร้างรายละเอียดเอกสารไม่สำเร็จ: ${detailResponse.data.message || ""}`
              );
            }
          });

        await Promise.all(detailPromises);
        await handleUploadAttachments(whDocId);

        const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
        if (originUrl) {
          localStorage.removeItem("TreasuryDocumentsOrigin");
          navigate(originUrl);
        } else {
          callInitialize();
        }
        // ไม่แสดง alert success หลังบันทึกสำเร็จ
      }
    } catch (error) {
      getAlert("FAILED", error.response?.data?.message || error.message);
    }
  };

  const handleUpdate = async () => {
    console.debug("Begin handleUpdate");
  };

  const handleCancel = async () => {
    try {
      if (docNoPrefix === "CNT" && !formMasterList.whDocNo) {
        const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
        if (originUrl) {
          localStorage.removeItem("TreasuryDocumentsOrigin");
          navigate(originUrl);
        } else {
          callInitialize();
        }
        return;
      }

      // ข้อมูลหลักที่จะส่งไปยัง API
      const formMasterData = {
        wh_doc_no: formMasterList.whDocNo,
        wh_doc_status: parseCurrency("13", 10),
        comp_id: window.localStorage.getItem("company"),
      };

      // ส่งข้อมูลหลักไปยัง API
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/cancel-wh-doc-h`,
        formMasterData,
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );

      const originUrl = localStorage.getItem("TreasuryDocumentsOrigin");
      if (originUrl) {
        localStorage.removeItem("TreasuryDocumentsOrigin");
        navigate(originUrl);
      } else {
        callInitialize();
      }
      getAlert(response.data.status, response.data.message);
    } catch (error) {
      getAlert("FAILED", error.response?.data?.message || error.message);
    }
  };

  return (
    <>
      <style>{`
        .form-wdp-page .created-info-input {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
        .form-wdp-page .created-info-input:disabled {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
      `}</style>
      <Breadcrumbs
        page={formMasterList.whDocNo || whDocNo}
        items={[
          { name: "คลังสินค้า", url: "/Warehouse" },
          { name: "เอกสารงานคลัง", url: localStorage.getItem("TreasuryDocumentsOrigin") || "/treasury-documents" },
          { name: mode === "U" ? "เรียกดู" + name : "สร้าง" + name, url: "#" },
        ]}
      />
      <div
        className="body hidden-scrollbar form-wdp-page"
        style={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <div class="container-fluid mt-2 mb-4">
          <div className="row mb-1">
            <div className="col-4">
              <div className="d-flex">
                <label>วันที่เอกสาร : </label>
                <Datetime
                  className="input-spacing-input-date"
                  name="whDocDate"
                  value={parseThaiDateToMoment(formMasterList.whDocDate)}
                  onChange={(date) => handleChangeDateMaster(date, "whDocDate")}
                  dateFormat="DD-MM-YYYY"
                  timeFormat={false}
                  renderInput={(props, openCalendar) => (
                    <input
                      {...props}
                      className={`${props.className || ""} created-info-input`.trim()}
                      readOnly={true}
                      disabled={
                        mode === "U" ||
                        (docNoPrefix === "CNT" && countHeaderStatus === WH_CNT_STATUS_COMPLETED)
                      }
                      value={formatDateToThai(formMasterList.whDocDate)}
                      onClick={openCalendar}
                    />
                  )}
                />
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex">
                <label className="">วันที่สร้าง : </label>
                <input
                  type="text"
                  className="form-control input-spacing created-info-input"
                  value={formMasterList.whDocCreatedDate}
                  disabled={true}
                />
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex">
                <label>สร้างโดย : </label>
                <input
                  type="text"
                  className="form-control input-spacing created-info-input"
                  value={formMasterList.whDocCreatedBy}
                  disabled={true}
                />
              </div>
            </div>
          </div>
          {docNoPrefix === "CNT" && countHeaderStatus != null && (
            <div className="row mb-1">
              <div className="col-4">
                <div className="d-flex">
                  <label>สถานะ : </label>
                  <input
                    type="text"
                    className="form-control input-spacing"
                    readOnly
                    value={
                      countHeaderStatus === WH_CNT_STATUS_PENDING
                        ? "รอบันทึกผล"
                        : countHeaderStatus === WH_CNT_STATUS_COMPLETED
                          ? "บันทึกผลแล้ว"
                          : String(countHeaderStatus)
                    }
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="d-flex">
                  <label className="">วันที่บันทึกผล : </label>
                  <input
                    type="text"
                    className="form-control input-spacing created-info-input"
                    value={formMasterList.whDocCountCompletedDate || ""}
                    disabled={true}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="d-flex">
                  <label>บันทึกผลโดย : </label>
                  <input
                    type="text"
                    className="form-control input-spacing created-info-input"
                    value={formMasterList.whDocCountCompletedBy || ""}
                    disabled={true}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="row mb-1">
            <div className="col-4">
              <div className="d-flex">
                <label>หมายเหตุ : </label>
                <input
                  type="text"
                  className="form-control input-spacing"
                  name="whDocRemark"
                  value={formMasterList.whDocRemark || ""}
                  onChange={handleChangeMaster}
                  disabled={docNoPrefix === "CNT" && countHeaderStatus === WH_CNT_STATUS_COMPLETED}
                />
              </div>
            </div>
            <div className="col-4" />
            <div className="col-4" />
          </div>
          <hr />
        </div>
        <div className="container-fluid my-3">
          <div className="row">
            <div className="col-12">
              <ItemWhTable
                mode={mode}
                showItemModal={showItemModal}
                handleItemClose={handleItemClose}
                itemDataList={itemDataList}
                onRowSelectItem={onRowSelectItem}
                formDetailList={formDetailList}
                handleItemShow={handleItemShow}
                handleQuantityChange={handleQuantityChange}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                handleRemoveRow={handleRemoveRow}
                formatCurrency={formatCurrency}
                disabled={
                  docNoPrefix === "CNT" && countHeaderStatus === WH_CNT_STATUS_COMPLETED
                }
                whDataList={whDataList}
                allOnhandData={allOnhandData}
                onWhChangePerRow={handleWarehouseChangePerRow}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
              />
            </div>
          </div>
        </div>
      </div>
      <FormAction
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onCancel={handleCancel}
        mode={mode}
        disabled={docNoPrefix === "CNT" && countHeaderStatus === WH_CNT_STATUS_COMPLETED}
        footer={true}
        setMode={(m) => { if (m === "S") callInitialize(); }}
        submitButtonLabel={docNoPrefix === "CNT" ? "บันทึกผล" : "บันทึก"}
        preSubmitAction={
          docNoPrefix === "CNT" && countHeaderStatus !== WH_CNT_STATUS_COMPLETED ? (
            <button
              type="button"
              className="btn btn-lg shadow text-white"
              style={{
                marginLeft: "20px",
                backgroundColor: "#ca8a04",
                fontSize: "15px",
              }}
              onClick={() => saveCountDocument("draft")}
            >
              บันทึกร่าง (รอบันทึกผล)
            </button>
          ) : null
        }
        showCancelButton={
          !(docNoPrefix === "CNT" && countHeaderStatus === WH_CNT_STATUS_COMPLETED)
        }
      />
    </>
  );
};

export default FormWDP;
